import { useRef, useState, useEffect } from "react";
import {
  FileSignature,
  MapPin,
  ShieldCheck,
  Package,
  TrendingUp,
  Search,
  UserCheck,
  ClipboardList,
  CalendarCheck,
  Banknote,
  ScanEye,
  Radio,
} from "lucide-react";
 
const PILLARS = [
  {
    num: "01",
    label: "Match",
    title: "Right creator for your space",
    body: "We handpick creators who fit your brand, your city, and your audience. No random influencers. No guesswork.",
    icon: Search,
  },
  {
    num: "02",
    label: "Contract",
    title: "Everything in writing",
    body: "Signed contracts before anything moves. Deliverables, timelines, usage rights — all documented and agreed upfront.",
    icon: FileSignature,
  },
  {
    num: "03",
    label: "Visit",
    title: "Creator comes to your location",
    body: "The creator visits your store or space, experiences it as a real customer would, and films it beautifully.",
    icon: MapPin,
  },
  {
    num: "04",
    label: "Quality",
    title: "We review everything",
    body: "Content passes through our quality system before delivery. If it doesn't meet the brief, we fix it.",
    icon: ShieldCheck,
  },
  {
    num: "05",
    label: "Deliver",
    title: "Content lands in your hands",
    body: "Edited, ready-to-post content delivered on schedule. You own it forever — no licensing, no strings.",
    icon: Package,
  },
  {
    num: "06",
    label: "Results",
    title: "Customers through your door",
    body: "Foot traffic, bookings, walk-ins. The only metrics that matter to a local business.",
    icon: TrendingUp,
  },
];
 
const OPERATIONS = [
  {
    title: "Creator sourcing & vetting",
    body: "We review every creator before they ever step into a client's space.",
    icon: UserCheck,
  },
  {
    title: "Contract management",
    body: "Both sides signed before any shoot. No ambiguity on deliverables or rights.",
    icon: ClipboardList,
  },
  {
    title: "Visit coordination",
    body: "Scheduling, briefing, on-time arrival — we make sure the creator shows up prepared.",
    icon: CalendarCheck,
  },
  {
    title: "Payment & escrow",
    body: "Funds held until deliverables confirmed. Creator gets paid when you're satisfied.",
    icon: Banknote,
  },
  {
    title: "Quality assurance",
    body: "Every video and image reviewed by our team before it reaches you.",
    icon: ScanEye,
  },
  {
    title: "Campaign tracking",
    body: "A live tracking link so you always know exactly where your campaign stands.",
    icon: Radio,
  },
];
 
export default function HowWeWork() {
  const trackRef = useRef(null);
  const stepRefs = useRef([]);
  const [fillPercent, setFillPercent] = useState(0);
  const [activeIndex, setActiveIndex] = useState(0);
 
  useEffect(() => {
    function handleScroll() {
      if (!trackRef.current || stepRefs.current.length === 0) return;
 
      const viewportCenter = window.innerHeight * 0.5;
 
      // Determine active step: the last step whose marker has passed the viewport center
      let newActiveIndex = 0;
      for (let i = 0; i < stepRefs.current.length; i++) {
        const el = stepRefs.current[i];
        if (!el) continue;
        const rect = el.getBoundingClientRect();
        if (rect.top <= viewportCenter) {
          newActiveIndex = i;
        }
      }
      setActiveIndex(newActiveIndex);
 
      // Determine fill percentage along the track based on scroll position
      const trackRect = trackRef.current.getBoundingClientRect();
      const trackHeight = trackRect.height;
      const scrolled = viewportCenter - trackRect.top;
      const percent = Math.min(100, Math.max(0, (scrolled / trackHeight) * 100));
      setFillPercent(percent);
    }
 
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, []);
 
  return (
    <>
    <section
      id="how-we-work"
      data-testid="how-we-work-section"
      className="py-24 sm:py-32 bg-[#0D3A35]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-8">
          {/* Left: sticky headline */}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-32">
              <p className="text-xs uppercase tracking-[0.22em] text-[#9CC07B] font-semibold">
                What we manage
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl leading-[0.98] mt-4 text-white">
                The entire
                <br />
                <span className="text-[#9CC07B]">creator-to-customer</span>
                <br />
                journey.
              </h2>
              <p className="mt-6 text-lg leading-relaxed text-[#F6F5F1]/70 max-w-md">
                From finding the right creator to the moment new customers
                walk through your door — Lychee handles every step. You
                focus on running your business. We handle the rest.
              </p>
            </div>
          </div>
 
          {/* Right: scroll-linked timeline */}
          <div className="lg:col-span-7">
            <div ref={trackRef} className="relative">
              {/* Track background line */}
              <div
                className="absolute left-[22px] top-2 bottom-2 w-px bg-[#F6F5F1]/15"
                aria-hidden="true"
              />
              {/* Track fill line, grows with scroll */}
              <div
                className="absolute left-[22px] top-2 w-px bg-[#9CC07B] transition-[height] duration-150 ease-out"
                style={{ height: `${fillPercent}%` }}
                aria-hidden="true"
              />
 
              <ol className="relative">
                {PILLARS.map((p, i) => {
                  const Icon = p.icon;
                  const isActive = i <= activeIndex;
                  return (
                    <li
                      key={p.num}
                      ref={(el) => (stepRefs.current[i] = el)}
                      data-testid={`pillar-${p.num}`}
                      className={`relative pl-20 ${
                        i !== PILLARS.length - 1 ? "pb-14 sm:pb-16" : ""
                      }`}
                    >
                      {/* Numbered marker */}
                      <span
                        className={`absolute left-[6px] top-0 w-8 h-8 rounded-full border flex items-center justify-center font-serif text-sm transition-colors duration-300 ${
                          isActive
                            ? "border-[#9CC07B] text-[#9CC07B] bg-[#0D3A35]"
                            : "border-[#F6F5F1]/25 text-[#F6F5F1]/40 bg-[#0D3A35]"
                        }`}
                      >
                        {i + 1}
                      </span>
 
                      <div
                        className={`flex items-center gap-3 transition-opacity duration-300 ${
                          isActive ? "opacity-100" : "opacity-50"
                        }`}
                      >
                        <h3 className="font-serif text-2xl text-white leading-tight">
                          {p.title}
                        </h3>
                        <Icon
                          className={`w-4 h-4 shrink-0 hidden sm:block transition-colors duration-300 ${
                            isActive ? "text-[#9CC07B]" : "text-[#F6F5F1]/30"
                          }`}
                          strokeWidth={1.6}
                        />
                      </div>
                      <p
                        className={`mt-2 text-sm leading-relaxed max-w-md transition-opacity duration-300 ${
                          isActive ? "text-[#F6F5F1]/70" : "text-[#F6F5F1]/40"
                        }`}
                      >
                        {p.body}
                      </p>
                    </li>
                  );
                })}
              </ol>
            </div>
          </div>
        </div>
      </div>
    </section>
 
    <section className="py-24 sm:py-32 bg-[#F6F5F1]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 mb-12">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[#276152] font-semibold">
              What we take off your plate
            </p>
            <h2 className="font-serif text-4xl sm:text-5xl leading-[0.95] mt-4 text-[#0D3A35]">
              Every operational detail,
              <br />
              <span className="text-[#276152]">handled.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 self-start lg:pt-3">
            <p className="text-lg leading-relaxed text-[#0D3A35]/75">
              The parts that usually break a brand-creator partnership — we
              run them so they never reach you.
            </p>
          </div>
        </div>
 
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#B1B7AB]/40 border border-[#B1B7AB]/40 rounded-2xl overflow-hidden">
          {OPERATIONS.map((op) => {
            const Icon = op.icon;
            return (
              <div
                key={op.title}
                className="bg-[#FFFFFF] p-8 lg:p-10 flex flex-col gap-4 min-h-[180px]"
              >
                <div className="flex items-center gap-3">
                  <Icon className="w-4 h-4 text-[#276152] shrink-0" strokeWidth={1.6} />
                  <h3 className="font-serif text-xl text-[#0D3A35] leading-tight">
                    {op.title}
                  </h3>
                </div>
                <p className="text-sm leading-relaxed text-[#0D3A35]/65">
                  {op.body}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
    </>
  );
}
